import { publishClientSideMetrics } from "../services/boson/bosonClient";

// Type definitions
interface AxiosError {
    // status is added in top level to cover both calls made via 
    // boson and direct API calls
    status?: number;
    response?: {
        status: number;
    };
    request?: any;
}


interface MetricRequest {
  processName: string;
  metricName: string;
  metricValue: number;
  metricType: string;
}

// Constants with proper typing
export const ERROR_COUNT_METRIC = "javascript" as const; // "Error" is appended in publish method
export const ONE_BOX_IDENTIFIER = "ONEBOX" as const;

export const MetricType = {
    SUCCESS: true,
    FAILED: false
} as const;

enum MetricTypeEnum {
    COUNT = "count",
    TIME = "time"
}


export const RCC_SHIFT_PLAN_GENERATION_PAGE = "RCC_SHIFT_PLAN_GENERATION_PAGE";
export const RCC_SHIFT_PLAN_SHIFT_ASSIGNMENT_PAGE = "RCC_SHIFT_PLAN_SHIFT_ASSIGNMENT_PAGE";
export const RCC_SHIFT_PLAN_ROSTER_AND_RATES_TAB = "RCC_SHIFT_PLAN_ROSTER_AND_RATES_TAB";
export const RCC_SHIFT_PLAN_INBOUND_TRAILER_TAB = "RCC_SHIFT_PLAN_INBOUND_TRAILER_TAB";
export const RCC_SHIFT_PLAN_VOLUMES_AND_WIP_TAB = "RCC_SHIFT_PLAN_VOLUMES_AND_WIP_TAB";
export const RCC_SHIFT_PLAN_REVIEW_AND_PUBLISH_TAB = "RCC_SHIFT_PLAN_REVIEW_AND_PUBLISH_TAB";
export const RCC_SHIFT_PLAN_FLOW_PLANNING_DASHBOARD_TAB = "RCC_SHIFT_PLAN_FLOW_PLANNING_DASHBOARD_TAB";
export const RCC_SHIFT_PLAN_WORK_ITEM_DETAIL_PAGE = "RCC_SHIFT_PLAN_WORK_ITEM_DETAIL_PAGE";
export const RCC_SHIFT_PLAN_WORK_ITEMS_DASHBOARD_PAGE = "RCC_SHIFT_PLAN_WORK_ITEMS_DASHBOARD_PAGE";
export const RCC_SHIFT_PLAN_STATION_ASSIGNMENT_PAGE = "RCC_SHIFT_PLAN_STATION_ASSIGNMENT_PAGE";

export const RCC_SHIFT_PLAN_LEADERBOARD_PAGE = "RCC_SHIFT_PLAN_LEADERBOARD_PAGE";
export const RCC_IMAGE_QUALITY_PAGE = "RCC_IMAGE_QUALITY_PAGE";
export const RCC_SHIFT_PLAN_CHECKOUT_TAB = "RCC_SHIFT_PLAN_CHECKOUT_TAB";

export const GENERATE_SHIFT_PLAN_API = "generateShiftPlanInput";
export const GENERATE_SHIFT_ASSIGNMENT_API = "generateShiftAssignmentInput";
export const GET_ALL_SHIFT_PLANS_API = "getAllShiftPlans";
export const GET_TARGET_AND_WIP_VOLUMES_API = "getTargetAndWIPVolume";
export const VERIFY_VOLUMES_API = "verifyVolume";
export const GET_RATES_AND_ROSTER_API = "getRatesAndRoster";
export const GET_SHIFT_PLAN_OUTPUT_API = "getShiftPlanOutput";
export const PUBLISH_SHIFT_PLAN_API = "publishShiftPlan";
export const FETCH_TRAILERS_API = "listYardTrailers";

export const GET_FLOW_MONITORING_DATA_API = "getFlowMonitoringData";
export const COMPARE_FLOW_MONITORING_DATA_API = "compareFlowMonitoringData";
export const SAVE_EOS_WASH_REPORT_API = "saveEosWashReport";
export const GET_PROCESS_FLOW_MAP_DATA_API = "getProcessFlowMapData";
export const GET_PROCESS_FLOW_METRICS_DATA_API = "getProcessFlowMetricsData";
export const GET_BUFFERS_PAGE_DATA_API = "getBuffersPageData";
export const GET_BUFFERS_DETAILS_DATA_API = "getBuffersDetailsData";

export const LOAD_WORK_ITEM_API = "loadWorkItem";
export const UPDATE_WORK_ITEM_STATE_API = "updateWorkItemState";
export const GET_WORK_ITEMS_API = "getWorkItems";
export const GET_WORK_ITEM_COUNTS_API = "getWorkItemCounts";
export const IS_VALID_STATION_ASSIGNMENT_OVERRIDE_API = "isValidStationAssignmentOverride";
export const PUBLISH_STATION_ASSIGNMENT_API = "publishStationAssignment";

export const GET_LEADERBOARD_API = "getLeaderboard";
export const GET_IMAGE_QUALITY_SITE_RANKINGS_API = "getImageQualitySiteRankings";
export const GET_IMAGE_QUALITY_REPORT_API = "getImageQualityReport";
export const GET_ASSOCIATE_IMAGE_QUALITY_API = "getAssociateImageQuality";
export const GET_EVIDENCE_PHOTO_LINKS_API = "getEvidencePhotoLinks";
export const DRY_RUN_SHIFT_PLAN_API = "dryRunShiftPlan";

// User-action metrics (not API calls) — emitted via logCounterMetric.
export const DOWNLOAD_SHIFT_PLAN_CSV = "downloadShiftPlanCsv";
export const IMAGE_QUALITY_PAGE_VIEW = "imageQualityPageView";
export const IMAGE_QUALITY_ASSOCIATE_VIEW = "imageQualityAssociateView";
export const ASSOCIATE_SCORECARDS_PAGE_VIEW = "associateScorecardsPageView";
export const ASSOCIATE_SCORECARD_DRAWER_OPEN = "associateScorecardDrawerOpen";

/**
 * Publishes API operation metrics: Latency, Errors, Counts and Station-Wise Counts.
 * @param {number} startTime - The start time timestamp
 * @param {string} stationCode - Station code identifier
 * @param {string} processName - Name of the process
 * @param {string} metricName - Name of the metric
 * @param {boolean} type - Success or failure type (MetricType.SUCCESS or MetricType.FAILED)
 * @param {AxiosError} [error] - Optional exception if any
 * @returns {void}
 */
export function logOperationalMetric(
    startTime: number,
    stationCode: string,
    processName: string,
    metricName: string,
    type: boolean,
    error?: AxiosError
): void {
    logLatencyMetric(startTime, processName, metricName);
    if (type === MetricType.SUCCESS) {
        logCounterMetric(stationCode, processName, metricName);
    } else {
        logErrorMetric(stationCode, processName, metricName, error);
    }
}

/**
 * Publishes error metric.
 * @param {string} stationCode - Station code identifier
 * @param {string} processName - Name of the process
 * @param {string} metricName - Name of the metric
 * @param {AxiosError} [error] - Optional exception if any
 * @returns {void}
 */
export function logErrorMetric(
    stationCode: string,
    processName: string,
    metricName: string,
    error?: AxiosError
): void {
    let errorMetric: string;
    const status = error?.response?.status || error?.status;
    if (status && status >= 500) {
        errorMetric = metricName + "FATAL";
    } else if (status && status >= 400) {
        errorMetric = metricName + "USER_EXCEPTION";
    } else if (error && error.request) {
        // axios client never received a response
        errorMetric = metricName + "NetworkError";
    } else {
        errorMetric = metricName + "Error";
    }
    logCounterMetric(stationCode, processName, errorMetric);
}

/**
 * Publishes counter metrics and station wise counter metrics.
 * @param {string} stationCode - Station code identifier
 * @param {string} processName - Name of the process
 * @param {string} metricName - Name of the metric
 * @returns {void}
 */
export function logCounterMetric(
    stationCode: string,
    processName: string,
    metricName: string
): void {
    const combinedProcessName = `${stationCode}_${processName}`;
    const countMetricReq = publishClientSideMetricsRequest(processName, metricName, 1, MetricTypeEnum.COUNT);
    const stationWiseCountMetric = publishClientSideMetricsRequest(combinedProcessName, metricName, 1, MetricTypeEnum.COUNT);
    publishClientSideMetrics(countMetricReq);
    publishClientSideMetrics(stationWiseCountMetric);
}

/**
 * Publishes latency metrics
 * @param {number} startTime - The start time timestamp
 * @param {string} processName - Name of the process
 * @param {string} metricName - Name of the metric
 * @returns {void}
 */
export function logLatencyMetric(
    startTime: number,
    processName: string,
    metricName: string
): void {
    const metricValue = getTimeDiff(startTime);
    const timeMetricReq = publishClientSideMetricsRequest(processName, metricName, metricValue, MetricTypeEnum.TIME);
    console.log("time metric request",timeMetricReq)
    publishClientSideMetrics(timeMetricReq);
}

/**
 * Calculates the time difference from a given timestamp.
 * @param {number} time - The start time timestamp
 * @returns {number} Time difference in milliseconds
 */
function getTimeDiff(time: number): number {
    return Date.now() - time;
}

/**
 * Creates a client-side metrics request object.
 * @param {string} processName - Name of the process
 * @param {string} metricName - Name of the metric
 * @param {number} metricValue - Value of the metric
 * @param {string} metricType - Type of the metric (count or time)
 * @returns {MetricRequest} Metric request object
 */
function publishClientSideMetricsRequest(
    processName: string,
    metricName: string,
    metricValue: number,
    metricType: string
): MetricRequest {
    return {
        processName,
        metricName,
        metricValue,
        metricType
    };
}